import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { env } from '@/config/env';
import { isPrivateRoute } from '@/donor/routes';

import { useLanguage } from './language-context';
import { intlLocales } from './translations';

export const usePageMetadata = (
  heading?: string,
  description?: string | null,
  image?: string | null,
): void => {
  const { locale, t } = useLanguage();
  const { pathname, search } = useLocation();
  useEffect(() => {
    const title = heading ? `${heading} · ${t.appName}` : t.appName;
    document.title = title;
    const privatePage = isPrivateRoute(pathname);
    const canonical = new URL(privatePage ? '/' : pathname, env.publicSiteUrl);
    const unit = new URLSearchParams(search).get('projectUnit');
    if (!privatePage && pathname.startsWith('/o/') && unit)
      canonical.searchParams.set('projectUnit', unit);
    const imageUrl = image?.startsWith('https://') ? image : `${env.publicSiteUrl}/og.png?v=3`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (privatePage) link?.remove();
    else {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.append(link);
      }
      link.href = canonical.href;
    }
    const values: Record<string, string> = {
      'meta[property="og:url"]': canonical.href,
      'meta[property="og:image"]': imageUrl,
      'meta[name="twitter:image"]': imageUrl,
      'meta[name="description"]': description || t.metaDescription,
      'meta[property="og:title"]': title,
      'meta[property="og:site_name"]': t.appName,
      'meta[property="og:image:alt"]': t.appName,
      'meta[name="twitter:title"]': title,
      'meta[property="og:description"]': description || t.socialDescription,
      'meta[name="twitter:description"]': description || t.socialDescription,
      'meta[property="og:locale"]': intlLocales[locale].replace('-', '_'),
    };
    for (const [selector, content] of Object.entries(values)) {
      document.querySelector(selector)?.setAttribute('content', content);
    }
  }, [heading, description, image, locale, t, pathname, search]);
};
